import { Button, FormLabel, Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { ExportStatus, UseExportDataReturn } from "hooks/useExportData";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useTenant } from "hooks/useTenant";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { getChemicalInventoryExportFileName } from "util/chemicalInventoryExportFileName";

import { Dialog } from "./Dialog";

type FormValues = {
  allReportingYears: boolean;
  reportingYear: number;
};

export function ChemicalInventoryExportDialog({
  open,
  onClose,
  tenantId,
  facilityId,
  facilityName,
  filter,
  startExport,
  status,
}: {
  open: boolean;
  onClose: () => void;
  tenantId?: string;
  facilityId?: string;
  facilityName?: string;
  filter?: Record<string, unknown>;
  startExport: UseExportDataReturn["startExport"];
  status: ExportStatus;
}) {
  const { tenant } = useTenant();
  const alerts = useAlerts();
  const selectedReportingYear = useSelectedReportingYear();

  const form = useForm<FormValues>({
    defaultValues: {
      allReportingYears: false,
      reportingYear: selectedReportingYear,
    },
  });
  const allReportingYears = form.watch("allReportingYears");

  useEffect(() => {
    if (open) {
      form.reset({
        allReportingYears: false,
        reportingYear: selectedReportingYear,
      });
    }
  }, [form, open, selectedReportingYear]);

  const handleSubmit = async (formData: FormValues) => {
    if (!tenantId) {
      return;
    }
    const reportingYear = formData.allReportingYears ? undefined : formData.reportingYear;
    const result = await startExport({
      exportType: "customer-chemical-inventory",
      tenantId,
      facilityId,
      filter,
      reportingYear,
      fileName: getChemicalInventoryExportFileName({
        tenantName: tenant?.name,
        facilityName,
        reportingYear,
      }),
    });
    if (result === "error") {
      return;
    }
    if (result === "queued") {
      alerts.success("Generating file...");
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="Export inventory"
      primaryActionButton={
        <Button
          onClick={form.handleSubmit(handleSubmit)}
          variant="contained"
          disabled={!tenantId}
          loading={status === "loading"}
        >
          Export
        </Button>
      }
    >
      <FormProvider {...form}>
        <RadioGroupField
          label=""
          name="allReportingYears"
          radioOptions={[
            { label: "For a specific reporting year", value: false },
            { label: "For all reporting years", value: true },
          ]}
          hideErrors
        />
        {!allReportingYears && (
          <Controller
            name="reportingYear"
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <Stack direction="row" alignItems="center" spacing={2}>
                <FormLabel>Reporting year</FormLabel>
                <TierIIYearPicker
                  persistQueryParam={false}
                  selectProps={{ size: "small" }}
                  value={value}
                  onChange={onChange}
                />
              </Stack>
            )}
          />
        )}
      </FormProvider>
    </Dialog>
  );
}
